import { apiUrl } from './config';

/**
 * Thin fetch wrapper.
 *
 * The token lives in sessionStorage, matching how the original app behaves —
 * closing the tab ends the shift's session, which is what a shared front-desk
 * machine wants.
 */
const TOKEN_KEY = 'token';
const BRANCH_KEY = 'branchId';

export const getToken = () => sessionStorage.getItem(TOKEN_KEY);
export const setToken = (t) => sessionStorage.setItem(TOKEN_KEY, t);
export const clearSession = () => {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(BRANCH_KEY);
};

export const getBranchId = () => {
  const v = sessionStorage.getItem(BRANCH_KEY);
  return v ? Number(v) : null;
};
/**
 * Changing branch has to invalidate everything already on screen.
 *
 * Every screen loads through useLoader, whose dependency arrays are about its
 * own filters — a date, a tab — and know nothing about which shop is selected.
 * Without this, switching from Song Wat to Sukhumvit left the previous shop's
 * orders on the table while the header claimed otherwise.
 */
let branchEpoch = 0;
const branchListeners = new Set();

export const getBranchEpoch = () => branchEpoch;
export const onBranchChange = (fn) => {
  branchListeners.add(fn);
  return () => branchListeners.delete(fn);
};

export const setBranchId = (id) => {
  const previous = sessionStorage.getItem(BRANCH_KEY);
  sessionStorage.setItem(BRANCH_KEY, String(id));
  if (previous !== String(id)) {
    branchEpoch += 1;
    branchListeners.forEach((fn) => fn(branchEpoch));
  }
};

export class ApiError extends Error {
  constructor(status, message) {
    super(message);
    this.status = status;
  }
}

/**
 * Every branch-scoped endpoint needs branchId. Rather than thread it through
 * each call site, it is appended here from the active branch unless the caller
 * passed one explicitly.
 */
async function request(path, { method = 'GET', body, params, branchScoped = true } = {}) {
  const url = new URL(apiUrl(path));

  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, v);
    });
  }
  if (branchScoped && !url.searchParams.has('branchId')) {
    const branchId = getBranchId();
    if (branchId) url.searchParams.set('branchId', branchId);
  }

  const headers = { 'Content-Type': 'application/json' };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const payload = body && branchScoped && body.branchId === undefined
    ? { ...body, branchId: getBranchId() }
    : body;

  const res = await fetch(url.toString(), {
    method,
    headers,
    body: payload ? JSON.stringify(payload) : undefined,
  });

  let data = null;
  try {
    data = await res.json();
  } catch {
    // A non-JSON body only matters when the request also failed.
  }

  if (!res.ok) {
    if (res.status === 401) clearSession();
    throw new ApiError(res.status, (data && data.message) || `คำขอล้มเหลว (${res.status})`);
  }
  return data;
}

export const api = {
  get: (path, params, opts) => request(path, { params, ...opts }),
  post: (path, body, opts) => request(path, { method: 'POST', body, ...opts }),
  patch: (path, body, opts) => request(path, { method: 'PATCH', body, ...opts }),
  put: (path, body, opts) => request(path, { method: 'PUT', body, ...opts }),
  del: (path, opts) => request(path, { method: 'DELETE', ...opts }),
};

// --- endpoint helpers, grouped by screen ----------------------------------

export const authApi = {
  login: (identifier, password) =>
    api.post('/api/users/login', { identifier, password }, { branchScoped: false }),
  me: () => api.get('/api/users/me', null, { branchScoped: false }),
  profile: () => api.get('/api/users/me/profile', null, { branchScoped: false }),
  updateProfile: (patch) => api.patch('/api/users/me/profile', patch, { branchScoped: false }),
  changePassword: (currentPassword, newPassword) =>
    api.post('/api/users/me/password', { currentPassword, newPassword }, { branchScoped: false }),
  changePin: (currentPassword, newPin) =>
    api.post('/api/users/me/pin', { currentPassword, newPin }, { branchScoped: false }),
  updateLocale: (locale) =>
    api.patch('/api/users/me/locale', { locale }, { branchScoped: false }),
  organizations: () => api.get('/api/users/me/organizations', null, { branchScoped: false }),
  switchOrganization: (organizationId) =>
    api.post('/api/users/me/organization', { organizationId }, { branchScoped: false }),
};

export const calendarApi = {
  board: (date) => api.get('/api/system/calendar', { date }),
  availability: () => api.get('/api/system/calendar/availability'),
  /* `at` + `minutes` narrow the answer to one treatment window, so a bed that
     is taken then comes back isFree: false. Without them every bed reads free,
     which is what the room view and the walk-in builder want. */
  beds: (serviceVariantId, { at, minutes } = {}) =>
    api.get('/api/system/calendar/beds', { serviceVariantId, at, minutes }),
  therapists: (at, minutes) =>
    api.get('/api/system/calendar/therapists', { at, minutes }),
  menu: () => api.get('/api/system/lookups/menu'),
  lookups: () => api.get('/api/system/lookups', null, { branchScoped: false }),
  createBooking: (payload) => api.post('/api/system/bookings', payload),
  turnAway: (payload) => api.post('/api/system/bookings/turn-away', payload),
  setAppointmentStatus: (id, status) =>
    api.patch(`/api/system/appointments/${id}/status`, { status }),
  updateAppointment: (id, patch) =>
    api.patch(`/api/system/appointments/${id}`, patch),
  cancelAppointment: (id, cancelReasonId) =>
    api.patch(`/api/system/appointments/${id}/status`, {
      status: 'cancelled',
      cancelReasonId,
    }),
};

/** เว็บจอง — the online requests the customer site files as drafts. */
export const bookingApi = {
  onlineGate: () => api.get('/api/system/bookings/online-gate'),
  requests: (status = 'draft') => api.get('/api/system/bookings/requests', { status }),
  // Confirming is also the moment the party is staffed — one call, so a request
  // can never end up booked with nobody assigned to it.
  confirmRequest: (id, assignments) =>
    api.post(`/api/system/bookings/${id}/confirm`, { assignments }),
  setStatus: (id, status, cancelNote, cancelReasonId) =>
    api.patch(`/api/system/bookings/${id}/status`, { status, cancelNote, cancelReasonId }),
};

/**
 * เข้างาน. There is no add or remove: a therapist is hired once for the shop and
 * is on every branch's board from then on, so only the shift itself is mutable.
 */
export const staffApi = {
  roster: (date, days = 3) => api.get('/api/system/staff/roster', { date, days }),
  setStatus: (id, status) => api.patch(`/api/system/staff/roster/${id}`, { status }),
  update: (id, patch) => api.patch(`/api/system/staff/roster/${id}`, patch),
  reorder: (date, shiftIds) => api.put('/api/system/staff/roster/order', { date, shiftIds }),
};

export const commissionApi = {
  outstanding: (scope = 'all') => api.get('/api/system/commission/outstanding',
    scope === 'all' ? { scope: 'all' } : { scope: 'branch', branchId: scope }),
  therapist: (id, periodEnd, scope = 'all') =>
    api.get(`/api/system/commission/therapist/${id}`,
      scope === 'all'
        ? { periodEnd, scope: 'all' }
        : { periodEnd, scope: 'branch', branchId: scope }),
  settle: (therapistId, periodEnd, scope = 'all') =>
    api.post('/api/system/commission/settle',
      scope === 'all'
        ? { therapistId, periodEnd, scope: 'all' }
        : { therapistId, periodEnd, scope: 'branch', branchId: scope }),
  history: (therapistId, scope = 'all') => api.get('/api/system/commission/history',
    scope === 'all'
      ? { therapistId, scope: 'all' }
      : { therapistId, scope: 'branch', branchId: scope }),
  addAdjustment: (payload) => api.post('/api/system/commission/adjustments', payload),
  deleteAdjustment: (id) => api.del(`/api/system/commission/adjustments/${id}`),
};

export const customerApi = {
  list: (params) => api.get('/api/system/customers', params, { branchScoped: false }),
  get: (id) => api.get(`/api/system/customers/${id}`, null, { branchScoped: false }),
  create: (payload) =>
    api.post('/api/system/customers', {
      ...payload,
      registeredBranchId: payload.registeredBranchId ?? getBranchId(),
    }, { branchScoped: false }),
  update: (id, patch) =>
    api.patch(`/api/system/customers/${id}`, patch, { branchScoped: false }),
  setPreferredTherapist: (id, therapistId) =>
    api.put(
      `/api/system/customers/${id}/preferred-therapist`,
      { therapistId },
      { branchScoped: false },
    ),
  search: (q) => api.get('/api/system/customers/search', { q }, { branchScoped: false }),
};

export const orderApi = {
  list: (params) => api.get('/api/system/orders', params),
  create: (payload) => api.post('/api/system/orders', payload),
  get: (id) => api.get(`/api/system/orders/${id}`),
  pay: (id, payload) => api.post(`/api/system/orders/${id}/payments`, payload),
  void: (id, reason) => api.post(`/api/system/orders/${id}/void`, { reason }),
};

export const beamApi = {
  device: (validate = false) =>
    api.get('/api/system/payments/beam/device', { validate: String(validate) }),
  connectDevice: (pairingCode) =>
    api.post('/api/system/payments/beam/device/connect', { pairingCode }),
  disconnectDevice: () =>
    api.post('/api/system/payments/beam/device/disconnect', {}),
  createIntent: (orderId, payload) =>
    api.post(`/api/system/orders/${orderId}/beam-intents`, payload),
  intent: (intentId) =>
    api.get(`/api/system/payments/beam/intents/${intentId}`),
};

export const cashierApi = {
  current: () => api.get('/api/system/cashier'),
  history: (from, to) => api.get('/api/system/cashier/history', { from, to }),
  open: (openingFloat) => api.post('/api/system/cashier/open', { openingFloat }),
  close: (countedCash, note) => api.post('/api/system/cashier/close', { countedCash, note }),
  movement: (payload) => api.post('/api/system/cashier/movements', payload),
  updateMovement: (id, patch) => api.patch(`/api/system/cashier/movements/${id}`, patch),
  categories: () => api.get('/api/system/cashier/categories', null, { branchScoped: false }),
};

export const reportApi = {
  dashboard: (from, to, scope = 'all') => api.get('/api/system/report/dashboard',
    scope === 'all'
      ? { from, to, scope: 'all' }
      : { from, to, scope: 'branch', branchId: scope }),
  daily: (from, to, scope = 'all') => api.get('/api/system/report/daily',
    scope === 'all'
      ? { from, to, scope: 'all' }
      : { from, to, scope: 'branch', branchId: scope }),
};

export const settingsApi = {
  store: () => api.get('/api/system/admin/store'),
  updateStore: (payload) => api.patch('/api/system/admin/store', payload),

  /* สาขา. Organisation-scoped, but still sent with the active branchId — that is
     what identifies the organisation and proves the caller belongs to it. */
  branches: () => api.get('/api/system/admin/branches'),
  createBranch: (payload) => api.post('/api/system/admin/branches', payload),
  closeBranch: (id) => api.del(`/api/system/admin/branches/${id}`),
  reopenBranch: (id) => api.patch(`/api/system/admin/branches/${id}/restore`),
  services: () => api.get('/api/system/admin/services'),
  products: () => api.get('/api/system/admin/products'),
  /* Only ตั้งค่า → หมอนวด wants the stood-down ones: it has a section for them
     and is where they are brought back. Everywhere else a retired therapist in
     the list would be someone the shop could accidentally book. */
  therapists: (params) => api.get('/api/system/admin/therapists', params),
  packages: () => api.get('/api/system/admin/packages'),
  promotions: () => api.get('/api/system/admin/promotions'),
  rooms: () => api.get('/api/system/admin/rooms'),
  general: () => api.get('/api/system/admin/general'),
  updateGeneral: (payload) => api.patch('/api/system/admin/general', payload),
  customerRefs: () => api.get('/api/system/admin/customer-refs'),
  receipt: () => api.get('/api/system/admin/receipt'),
  updateReceipt: (payload) => api.patch('/api/system/admin/receipt', payload),
  bookingChannels: () => api.get('/api/system/admin/booking-channels'),
  updateBookingChannelConfig: (payload) =>
    api.patch('/api/system/admin/booking-channels/config', payload),
  createBookingChannel: (payload) =>
    api.post('/api/system/admin/booking-channels/items', payload),
  updateBookingChannel: (id, payload) =>
    api.patch(`/api/system/admin/booking-channels/items/${id}`, payload),
  createPaymentChannel: (payload) =>
    api.post('/api/system/admin/payment-channels/items', payload),
  updatePaymentChannel: (id, payload) =>
    api.patch(`/api/system/admin/payment-channels/items/${id}`, payload),
  roles: () => api.get('/api/system/admin/roles'),

  // create — ปุ่ม "สร้าง…" on each settings section
  createService: (payload) => api.post('/api/system/admin/services', payload),
  updateServiceCommissions: (serviceId, commissions) =>
    api.patch(`/api/system/admin/services/${serviceId}/commissions`, { commissions }),
  createServiceCategory: (payload) => api.post('/api/system/admin/service-categories', payload),
  createProductCategory: (payload) => api.post('/api/system/admin/product-categories', payload),
  createProduct: (payload) => api.post('/api/system/admin/products', payload),
  adjustProductStock: (id, payload) => api.post(`/api/system/admin/products/${id}/stock`, payload),
  createPromotion: (payload) => api.post('/api/system/admin/promotions', payload),
  createPackage: (payload) => api.post('/api/system/admin/packages', payload),
  updatePackage: (id, payload) => api.patch(`/api/system/admin/packages/${id}`, payload),
  createTherapist: (payload) => api.post('/api/system/admin/therapists', payload),
  createRoom: (payload) => api.post('/api/system/admin/rooms', payload),
  createBed: (payload) => api.post('/api/system/admin/beds', payload),
  createNationality: (payload) => api.post('/api/system/admin/nationalities', payload),
  createOccupation: (payload) => api.post('/api/system/admin/occupations', payload),
  createRole: (payload) => api.post('/api/system/admin/roles', payload),
  createAgency: (payload) => api.post('/api/system/admin/agencies', payload),

  /**
   * ตั้งค่า edit / delete / restore.
   *
   * `entity` is one of the names the backend registry knows (services,
   * products, packages, promotions, therapists, rooms, beds, roles, agencies,
   * occupations, nationalities, cash-categories, cancel-reasons, variants).
   * Deleting retires the row where the table can hold a retired row, so the
   * response says which happened.
   */
  update: (entity, id, patch) => api.patch(`/api/system/admin/${entity}/${id}`, patch),
  remove: (entity, id) => api.del(`/api/system/admin/${entity}/${id}`),
  restore: (entity, id) => api.patch(`/api/system/admin/${entity}/${id}/restore`),

  /**
   * สิทธิ์เข้าใช้ — the accounts, not the role types `roles()` above returns.
   * These are not part of the entity registry `update`/`remove` speak to: the
   * accounts have their own guards (last owner, your own role) and their own
   * password route, so they get their own calls.
   */
  users: () => api.get('/api/system/admin/users'),
  createUser: (payload) => api.post('/api/system/admin/users', payload),
  updateUser: (id, patch) => api.patch(`/api/system/admin/users/${id}`, patch),
  deactivateUser: (id) => api.del(`/api/system/admin/users/${id}`),
  // POST with the password in the body — never a path or a query string, which
  // would leave it in the browser history and every proxy log on the way.
  setUserPassword: (id, newPassword) =>
    api.post(`/api/system/admin/users/${id}/password`, { newPassword }),
};

export const notificationApi = {
  list: () => api.get('/api/system/notifications'),
  markRead: (id) => api.post(
    id ? `/api/system/notifications/${id}/read` : '/api/system/notifications/read',
    {},
  ),
};

export const userApi = {
  setLocale: (locale) =>
    api.patch('/api/users/me/locale', { locale }, { branchScoped: false }),
};
